import React, { useState } from 'react';
import SortIcon from '@mui/icons-material/Sort';
import { ButtonBase, MenuItem, SvgIconProps } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { HeaderMenu } from '../Header/HeaderItems';
import useAuthProps from '../../../hooks/useAuthProps';

interface IProps {
  sorting?: string;
  /**
   * If `true`, the order button will be disabled.
   */
  disabled?: boolean;
  /**
   * Sort icon to use.
   */
  IconComponent?: React.ElementType<SvgIconProps>;
  /**
   * Callback fired when the component change order.
   *
   * @param {string} order Changed order
   * @param {string} orderBy Specifies key on which to sort.
   */
  onChange: (order: string, orderBy: string) => void;

  type: 'events' | 'alphabetical' | 'pastEvents' | 'expiration';
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const useStyles = makeStyles<void, 'button' | 'disabled'>()((theme) => ({
  disabled: {
    color: alpha(`${theme.palette.text.secondary}`, 0.74),
  },
  root: {
    ...theme.typography.button,
    display: 'inline-flex',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '120%',
  },
  button: {
    ...theme.typography.button,
    display: 'inline-flex',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '120%',
    marginLeft: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  icon: {
    userSelect: 'none',
    transform: 'rotateX(180deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  iconRotate: {
    transform: 'rotateX(0deg)',
  },
}));

const defaultSortingOrder = ['', '-'] as const;

const menuItems = [
  {
    value: 'session_date',
  },
  {
    value: 'popularity',
  },
  {
    value: 'start_at',
  },
];

const authentificatedMenuItems = [
  {
    value: 'session_date',
  },
  {
    value: 'recommendation_rating',
  },
  {
    value: 'popularity',
  },
  {
    value: 'start_at',
  },
];

const pastEventsMenuItems = [
  {
    value: 'start_at',
  },
];

const alphabeticalMenuItems = [
  {
    value: 'title',
  },
];

const expirationMenuItems = [
  {
    value: 'start_at',
  },
];

const SortBy: React.FC<React.PropsWithChildren<IProps>> = ({
  type = 'events',
  sorting = 'start_at',
  disabled = false,
  IconComponent = SortIcon,
  onChange,
}) => {
  const { classes, cx } = useStyles();
  const [t] = useTranslation();
  const [resultOrder, setResultOrder] = useState(sorting.startsWith('-') ? '-' : '');
  const [resultOrderBy, setResultOrderBy] = useState(sorting.startsWith('-') ? sorting.substring(1) : sorting);
  const [anchorEl, setAnchorEl] = useState(null);
  const { isAuthenticated } = useAuthProps();
  const handleClickOrder = () => {
    const [asc, desc] = defaultSortingOrder;
    const newOrder = resultOrder === asc ? desc : asc;
    setResultOrder(newOrder);
    onChange(newOrder, resultOrderBy);
  };
  const availableSortings = () => {
    if (type === 'pastEvents') {
      return pastEventsMenuItems;
    }

    if (type === 'alphabetical') {
      return alphabeticalMenuItems;
    }
    if (type === 'expiration') {
      return expirationMenuItems;
    }
    return isAuthenticated ? authentificatedMenuItems : menuItems;
  };

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent) => {
    if (availableSortings().length > 1) {
      setAnchorEl(e.currentTarget);
    } else handleClickOrder();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClick = (filterValue: string) => {
    setResultOrderBy(filterValue);
    onChange(resultOrder, filterValue);
    setAnchorEl(null);
  };

  return (
    <span
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      className={classes.root}
    >
      <span>{t<string>('common:general.sortBy')}:</span>
      <ButtonBase
        disableRipple
        disabled={disabled}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        className={cx(classes.button, { [classes.disabled]: disabled })}
        onClick={handleOpen}
      >
        {t(`common:general.ordering.by`, { context: resultOrderBy })}
      </ButtonBase>
      <ButtonBase
        disableRipple
        disabled={disabled}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        className={cx(classes.button, { [classes.disabled]: disabled })}
        onClick={handleClickOrder}
      >
        <IconComponent
          fontSize="small"
          color="inherit"
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          className={cx(classes.icon, { [classes.iconRotate]: resultOrder !== defaultSortingOrder[0] })}
        />
      </ButtonBase>
      <HeaderMenu open={open} onClose={handleClose} anchorEl={anchorEl}>
        {availableSortings().map((item) => (
          <MenuItem key={item.value} onClick={() => onClick(item.value)}>
            {t(`common:general.ordering.by`, { context: item.value })}
          </MenuItem>
        ))}
      </HeaderMenu>
    </span>
  );
};

export default SortBy;
